import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { useAuth } from '../../auth.jsx';
import { useLive } from '../../live.jsx';
import { countryOptions } from '../../countries.js';
import { INCOTERMS } from '../../productOptions.js';
import { Alert } from '../../components/ui.jsx';
import RequestCard from './RequestCard.jsx';
import './requests.css';

// Marketplace › "Demandes d'achat / Buying requests": filters in the URL
// (shared with the products tab: q, category; plus country, qmin, qmax,
// incoterm, posted, state). compact = the "All" tab's strip (6 cards, no
// extra filters).
const KEYS = ['country', 'qmin', 'qmax', 'incoterm', 'posted', 'state'];
export default function RequestsBrowse({ params, update, compact = false, onSeeAll }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  useLive(['requests'], () => setTick((x) => x + 1));
  const countries = useMemo(() => countryOptions(lang), [lang]);
  const q = params.get('q') || '';
  const category = params.get('category') || '';
  const page = Number(params.get('page')) || 1;
  const vals = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || '']));
  const key = JSON.stringify([q, category, page, vals, compact, lang, tick]);

  useEffect(() => {
    let alive = true;
    setError('');
    const qs = new URLSearchParams({ lang });
    if (q) qs.set('q', q);
    if (category) qs.set('category', category);
    if (compact) qs.set('size', '6');
    else {
      for (const k of KEYS) if (vals[k]) qs.set(k, vals[k]);
      if (page > 1) qs.set('page', page);
    }
    api(`/buying-requests?${qs}`).then((d) => alive && setData(d)).catch((e) => alive && setError(errorText(e)));
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const post = <Link className="btn btn-primary btn-sm" to={user ? '/account/buying-requests/new' : '/login?next=/account/buying-requests/new'}>{t('buying.post')}</Link>;

  if (compact) {
    if (!data || !data.items.length) return null;
    return (
      <section className="req-strip" aria-labelledby="req-strip-h">
        <div className="req-section-head">
          <h2 id="req-strip-h"><span className="wanted-badge">{t('buying.wanted')}</span> {t('buying.title')}</h2>
          <span className="req-actions">{data.total > data.items.length && <button type="button" className="btn btn-link btn-sm" onClick={onSeeAll}>{t('buying.home.all')} ({data.total}) →</button>}{post}</span>
        </div>
        <div className="req-grid">{data.items.map((r) => <RequestCard key={r.id} request={r} />)}</div>
      </section>
    );
  }

  return (
    <>
      <div className="req-section-head">
        <p className="muted" style={{ margin: 0 }}>{t('buying.lead')}</p>
        {post}
      </div>
      <div className="req-filters" role="group" aria-label={t('marketplace.filters')}>
        <label>{t('buying.filters.country')}
          <select value={vals.country} onChange={(e) => update({ country: e.target.value })}>
            <option value="">{t('buying.filters.anyCountry')}</option>
            {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <label>{t('buying.filters.qmin')}<input type="number" min="0" inputMode="decimal" value={vals.qmin} onChange={(e) => update({ qmin: e.target.value })} /></label>
        <label>{t('buying.filters.qmax')}<input type="number" min="0" inputMode="decimal" value={vals.qmax} onChange={(e) => update({ qmax: e.target.value })} /></label>
        <label>{t('buying.filters.incoterm')}
          <select value={vals.incoterm} onChange={(e) => update({ incoterm: e.target.value })}>
            <option value="">{t('buying.filters.anyIncoterm')}</option>
            {INCOTERMS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>{t('buying.filters.posted')}
          <select value={vals.posted} onChange={(e) => update({ posted: e.target.value })}>
            <option value="">{t('buying.filters.anyTime')}</option>
            {[7, 30, 90].map((n) => <option key={n} value={n}>{t('buying.filters.days', { n })}</option>)}
          </select>
        </label>
        <label>{t('buying.filters.state')}
          <select value={vals.state} onChange={(e) => update({ state: e.target.value })}>
            <option value="">{t('buying.filters.open')}</option>
            <option value="closed">{t('buying.filters.closed')}</option>
            <option value="all">{t('buying.filters.allStates')}</option>
          </select>
        </label>
      </div>
      {data && <p className="muted">{t('buying.filters.results', { n: data.total })}</p>}
      <Alert>{error}</Alert>
      {!data && !error && <p className="muted">{t('common.loading')}</p>}
      {data && !data.items.length && <div className="empty-state"><p>{q || category || KEYS.some((k) => vals[k]) ? t('buying.filters.none') : t('buying.filters.noneYet')}</p></div>}
      {data && data.items.length > 0 && <div className="req-grid">{data.items.map((r) => <RequestCard key={r.id} request={r} />)}</div>}
      {data && pages > 1 && (
        <nav className="pager" aria-label={t('marketplace.page', { n: page, total: pages })}>
          <button type="button" className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => update({ page: page - 1 })}>{t('marketplace.prev')}</button>
          <span className="muted">{t('marketplace.page', { n: page, total: pages })}</span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => update({ page: page + 1 })}>{t('marketplace.next')}</button>
        </nav>
      )}
    </>
  );
}
